import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router'
import {MainReducer, RootState, UserReducer} from 'setup'

import {PageLink} from '../../../core/PageData'
import {getAsideMenuLinks} from '../../sidebar/sidebar-menu/SidebarLinks'
import {TrailCrumb, dedupeConsecutive, resolveMenuTrail} from './breadcrumbTrail'

export type BreadcrumbTrail = {
  /** Translated, deduped ancestors; never includes the title itself. */
  ancestors: {title: string; path?: string}[]
  /** Page title, falling back to the matched sidebar item when the page set none. */
  title: string
}

type Args = {
  pageTitle?: string
  /** Manual crumbs from `<PageTitle breadcrumbs>`; used only when the sidebar has no match. */
  manualBreadcrumbs?: PageLink[]
}

/**
 * The breadcrumb trail for the current route, derived from the sidebar menu
 * tree so it reflects the same permissions and tenant titles the sidebar
 * shows. Pages the sidebar does not know fall back to their own
 * `<PageTitle breadcrumbs>` array.
 */
export const useBreadcrumbTrail = ({pageTitle, manualBreadcrumbs}: Args): BreadcrumbTrail => {
  const intl = useIntl()
  const {pathname} = useLocation()
  const mainReducer = useSelector<RootState>((s) => s.mainReducer) as MainReducer
  const userReducer = useSelector<RootState>((s) => s.userReducer) as UserReducer

  const match = useMemo(() => {
    const links = getAsideMenuLinks({mainReducer, userReducer})
    const raw = resolveMenuTrail(links, pathname)
    if (!raw) return null
    const translate = (c: TrailCrumb) => ({
      title: intl.messages[c.title] ? intl.formatMessage({id: c.title}) : c.title,
      path: c.path,
    })
    return {chain: dedupeConsecutive(raw.chain.map(translate)), exact: raw.exact}
  }, [mainReducer, userReducer, pathname, intl])

  return useMemo(() => {
    const title = pageTitle?.trim() ?? ''
    if (match && match.chain.length > 0) {
      const {chain, exact} = match
      const leaf = chain[chain.length - 1]
      // The page IS the sidebar item: its title wins over whatever the route
      // wrapper set (wrappers tend to give every child the module name).
      if (exact || !title || leaf.title === title) {
        return {ancestors: chain.slice(0, -1), title: leaf.title}
      }
      // The page is deeper than the sidebar goes (edit/detail) → whole chain leads to it.
      return {ancestors: chain, title}
    }
    const ancestors = (manualBreadcrumbs ?? [])
      .filter((b) => !b.isSeparator && b.title && b.title !== title)
      .map((b) => ({title: b.title, path: b.path || undefined}))
    return {ancestors: dedupeConsecutive(ancestors), title}
  }, [match, pageTitle, manualBreadcrumbs])
}
